import { Alert, Badge, Table } from "antd";
import {
  CollectorInput,
  DeviceOutput,
  SiteReadable as Site,
} from "../../../services/client/types.gen";
import { ColumnsType } from "antd/es/table/InternalTable";
import { sortBy } from "lodash";
import { Link } from "react-router-dom";
import { siteDetailsTabKeys } from "../siteDetailsKeys";
import { isReady } from "./utils";

type AssetValidationTableProps = {
  site: Site;
  isLoading?: boolean;
};

type AssetForTable = {
  name: string;
  type: "device" | "collector";
  hasError?: boolean;
  error?: string;
};

export const AssetValidationTable: React.FC<AssetValidationTableProps> = ({
  site,
  isLoading,
}) => {
  const configurationAdded = isReady(
    site.validation_status?.site_configuration_status?.configuration_added
      ?.status,
  );
  if (!configurationAdded) {
    return (
      <div className="w-full mb-4">
        <Alert
          message="Missing site configuration"
          description={
            <p>
              You have not yet added a site configuration.{" "}
              <Link
                to={`/sites/${site.site_id}?tab=${siteDetailsTabKeys["Site Configuration"]}`}
              >
                Add one now
              </Link>
            </p>
          }
          type="error"
          showIcon
        />
      </div>
    );
  }

  const optimizationConfigurationAdded = isReady(
    site.validation_status?.optimization_configuration_status?.status,
  );

  const assetsNotFoundInConfig =
    site.validation_status?.site_configuration_status?.configuration_complete
      ?.missing_assets;
  const notReceivingDataFrom =
    site.validation_status?.ingress_status?.receiving_measurement_data
      ?.not_receiving_data_from;
  const notReceivingAnyData = notReceivingDataFrom?.includes("*");
  const validationErrors =
    site.validation_status?.site_configuration_status?.valid_for_optimization
      ?.validation_errors;

  const assetHasErrors = (name: string) => {
    return (
      notReceivingDataFrom?.includes(name) ||
      notReceivingAnyData ||
      validationErrors?.includes(name) ||
      assetsNotFoundInConfig?.includes(name)
    );
  };

  const mapCollector = (collector: CollectorInput): AssetForTable[] => {
    const current: AssetForTable = {
      name: collector.name,
      type: "collector",
      hasError: assetHasErrors(collector.name),
    };
    if (!collector.children) {
      return [current];
    }

    return [current].concat(...collector.children.map(mapCollector));
  };

  const mapDevice = (device: DeviceOutput): AssetForTable => {
    return {
      name: device.name,
      hasError: assetHasErrors(device.name),
      type: "device",
    };
  };

  const mapAssetsNotFoundInConfigToTable = (name: string): AssetForTable => {
    return {
      name,
      hasError: assetHasErrors(name),
      type: "device",
    };
  };

  const getDescription = (asset: AssetForTable) => {
    if (assetsNotFoundInConfig?.includes(asset.name)) {
      return "Asset not found in site configuration";
    }
    if (notReceivingAnyData) {
      return "Not receiving data from any asset";
    }
    if (notReceivingDataFrom?.includes(asset.name)) {
      return "Not receiving data";
    }
    if (validationErrors?.includes(asset.name)) {
      return "Validation error";
    }
    return "Ok";
  };

  const columns: ColumnsType<AssetForTable> = [
    {
      title: "Asset",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <div>
          <Badge
            status={record.hasError ? "error" : "success"}
            className="mr-2"
          />{" "}
          {getDescription(record)}
        </div>
      ),
      defaultSortOrder: "ascend",
      sorter: (a, b) => {
        const aHasError = a.hasError;
        const bHasError = b.hasError;
        if (aHasError === bHasError) {
          return getDescription(a).localeCompare(getDescription(b));
        }
        return aHasError ? -1 : 1;
      },
    },
  ];

  if (!site.configurations?.site_configuration) {
    return (
      <Table<AssetForTable>
        columns={columns}
        className="w-full"
        pagination={{
          defaultPageSize: 20,
        }}
      />
    );
  }

  const allAssets = sortBy(
    [
      ...site.configurations.site_configuration.collector_list.flatMap(
        mapCollector,
      ),
      ...site.configurations.site_configuration.device_list.map(mapDevice),
      ...(assetsNotFoundInConfig ?? []).map(mapAssetsNotFoundInConfigToTable),
    ],
    (asset: AssetForTable) => (asset.hasError ? 0 : 1),
  );

  return (
    <>
      {!optimizationConfigurationAdded ? (
        <div className="w-full mb-4">
          <Alert
            message="Missing optimization configuration"
            description={
              <p>
                You have not yet added an optimization configuration.{" "}
                <Link
                  to={`/sites/${site.site_id}?tab=${siteDetailsTabKeys.Settings}`}
                >
                  Add one now
                </Link>
              </p>
            }
            type="error"
            showIcon
          />
        </div>
      ) : null}
      {!isReady(site.validation_status?.status) && (
        <div className="w-full mb-4">
          <Alert
            className="text-center"
            message={
              <div className="py-2">
                <div>
                  There seem to be some issues with this site. Take a look in
                  the table below for details.
                </div>
                <div>
                  Go to the{" "}
                  <Link
                    to={`/sites/${site.site_id}?tab=${siteDetailsTabKeys["Site Configuration"]}`}
                  >
                    site configuration tab
                  </Link>{" "}
                  to edit the site configuration.
                </div>
              </div>
            }
            type="error"
          />
          {isLoading && (
            <Alert
              className="text-center mt-4"
              message="Revalidating site ..."
              type="info"
            />
          )}
        </div>
      )}

      <Table<AssetForTable>
        columns={columns}
        className="w-full"
        dataSource={allAssets}
        pagination={{
          defaultPageSize: 20,
        }}
        rowKey="name"
      />
    </>
  );
};
